import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../api/axios";
import { CLASS_GROUPS, getSubjectsForClass } from "../data/education";
import Pagination from "../components/Pagination";
import VerifiedBadge from "../components/VerifiedBadge";
import { formatTeachingStructure } from "../utils/teachingFormat";

// ─── constants ────────────────────────────────────────────────────────────────
const BOARDS      = ["CBSE", "ICSE", "UP Board"];
const ALL_CLASSES = CLASS_GROUPS.flatMap(g => g.classes);

// ─── helpers (unchanged from original) ────────────────────────────────────────
const avgR = a => a?.length ? +(a.reduce((x, y) => x + y, 0) / a.length).toFixed(1) : 0;

const classMatches = (teacherClass, searchClass) => {
  if (!teacherClass || !searchClass) return true;
  const tc = teacherClass.toLowerCase();
  const sc = searchClass.toLowerCase();
  if (tc.includes("all")) return true;
  const searchNum = parseInt(sc.match(/\d+/)?.[0]);
  if (!searchNum) return tc.includes(sc); // Nursery, LKG, UKG — no number, use string match
  const rangeMatch = tc.match(/(\d+)\s*[-–]+\s*(\d+)/);
  if (rangeMatch) {
    const min = parseInt(rangeMatch[1]);
    const max = parseInt(rangeMatch[2]);
    if (searchNum >= min && searchNum <= max) return true;
  }
  const nums = tc.match(/\d+/g)?.map(Number) || [];
  return nums.includes(searchNum);
};

const teacherMatchesSearch = (teacher, searchClass, searchSubjects) => {
  if (!searchClass && searchSubjects.length === 0) return true;
  if (teacher.teaching?.length > 0) {
    return teacher.teaching.some(entry => {
      const classMatch = !searchClass || classMatches(entry.class, searchClass);
      if (!classMatch) return false;
      if (searchSubjects.length === 0) return true;
      if (entry.subjects.includes("All")) return true;
      return searchSubjects.every(s => entry.subjects.includes(s));
    });
  }
  const classMatch = !searchClass || (
    Array.isArray(teacher.classes)
      ? teacher.classes.some(c => classMatches(c, searchClass))
      : classMatches(teacher.classes, searchClass)
  );
  if (!classMatch) return false;
  if (searchSubjects.length === 0) return true;
  if (teacher.subjects?.includes("All Subjects")) return true;
  return searchSubjects.every(s => teacher.subjects?.includes(s));
};

// ─── styles ───────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
  * { font-family: 'Inter', sans-serif; box-sizing: border-box; }

  @keyframes fadeInUp  { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
  @keyframes shimmer   { 0% { background-position:-600px 0; } 100% { background-position:600px 0; } }
  @keyframes pulse-dot { 0%,100% { opacity:1; } 50% { opacity:.3; } }
  .sp { background:#080812; min-height:100vh; color:#e2e8f0; }

  /* ── Header ── */
  .sp-header {
    background: linear-gradient(180deg,#0d0d1f 0%,#080812 100%);
    border-bottom: 1px solid #1a1a35;
    padding: 28px 24px 24px;
    text-align: center;
  }
  .sp-header h1 {
    font-size: clamp(24px,4vw,38px); font-weight:800; letter-spacing:-1px;
    color:#fff; margin:0 0 8px; line-height:1.1;
    text-shadow: 0 0 40px rgba(249,115,22,.15);
  }
  .sp-header h1 span { color:#f97316; }
  .sp-header p { font-size:14px; color:#cbd5e1; margin:0; }


  /* ── Main layout ── */
  .sp-layout {
    display: grid;
    grid-template-columns: 296px 1fr;
    gap: 28px;
    max-width: 1200px;
    margin: 0 auto;
    padding: 28px 24px 48px;
    align-items: start;
  }

  /* ── Sidebar ── */
  .sp-sidebar {
    background: #0d0d1f;
    border: 1px solid #1a1a35;
    border-radius: 18px;
    position: sticky;
    top: 20px;
    overflow: hidden;
  }
  .sidebar-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 18px 14px; border-bottom: 1px solid #1a1a35;
  }
  .sidebar-title { font-size:13px; font-weight:700; color:#fff; letter-spacing:.5px; }
  .reset-link {
    font-size:11px; color:#cbd5e1; cursor:pointer; font-weight:600;
    background:none; border:none; font-family:inherit; padding:0;
    transition: color .15s;
  }
  .reset-link:hover { color:#f97316; }

  /* ── Filter content (shared sidebar + drawer) ── */
  .filter-content { padding: 6px 0; }

  .filter-step {
    display: flex; gap: 12px; padding: 14px 18px;
    border-bottom: 1px solid #12121f;
  }
  .filter-step:last-child { border-bottom: none; }

  .step-num {
    width: 22px; height: 22px; border-radius: 50%;
    background: #1e1e38; color: #94a3b8; font-size: 10px; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; margin-top: 1px; transition: background .2s, color .2s;
  }
  .step-done { background: rgba(249,115,22,.2) !important; color: #f97316 !important; }

  .step-body { flex: 1; min-width: 0; }
  .step-label {
    font-size: 11px; font-weight: 700; color: #e2e8f0;
    letter-spacing: 1px; text-transform: uppercase; margin-bottom: 10px;
    display: flex; align-items: center; gap: 6px;
  }
  .opt-tag {
    font-size: 9px; font-weight: 500; color: #94a3b8;
    background: #161626; border: 1px solid #252545; border-radius: 4px;
    padding: 1px 5px; text-transform: none; letter-spacing: 0;
  }

  /* Class select toggle button */
  .class-select-btn {
    width: 100%; padding: 9px 12px; border-radius: 9px;
    border: 1px solid #1e1e3a; background: #080812; color: #64748b;
    font-size: 13px; font-weight: 500; cursor: pointer; text-align: left;
    font-family: inherit; display: flex; align-items: center; justify-content: space-between;
    transition: border-color .2s, color .2s;
  }
  .class-select-btn:hover { border-color: rgba(249,115,22,.4); color: #e2e8f0; }
  .class-selected-chip {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(249,115,22,.15); border: 1px solid rgba(249,115,22,.4);
    color: #f97316; font-size: 12px; font-weight: 700; padding: 5px 12px;
    border-radius: 20px; cursor: default;
  }
  .class-clear-x {
    cursor: pointer; opacity: .6; font-size: 11px; font-weight: 700;
    transition: opacity .15s;
  }
  .class-clear-x:hover { opacity: 1; }

  /* Class chips grid */
  .class-grid {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px;
    margin-top: 8px;
  }
  .class-chip {
    padding: 6px 4px; border-radius: 8px; border: 1px solid #1e1e3a;
    background: transparent; color: #cbd5e1; font-size: 11px; font-weight: 600;
    cursor: pointer; text-align: center; transition: all .15s; font-family: inherit;
  }
  .class-chip:hover { border-color: rgba(249,115,22,.4); color: #fb923c; }
  .class-chip-on {
    background: #f97316 !important; border-color: #f97316 !important;
    color: #fff !important; box-shadow: 0 2px 10px rgba(249,115,22,.3);
  }

  /* Board chips */
  .board-chips { display: flex; gap: 5px; flex-wrap: wrap; }
  .board-chip {
    padding: 5px 11px; border-radius: 7px; border: 1px solid #1e1e3a;
    background: transparent; color: #cbd5e1; font-size: 11px; font-weight: 600;
    cursor: pointer; transition: all .15s; font-family: inherit;
  }
  .board-chip:hover { border-color: rgba(99,102,241,.4); color: #818cf8; }
  .board-chip-on {
    background: rgba(99,102,241,.15) !important; border-color: #818cf8 !important;
    color: #818cf8 !important;
  }

  /* Subject chips */
  .subj-chips { display: flex; gap: 5px; flex-wrap: wrap; }
  .subj-chip {
    padding: 5px 11px; border-radius: 7px; border: 1px solid #1e1e3a;
    background: transparent; color: #cbd5e1; font-size: 11px; font-weight: 600;
    cursor: pointer; transition: all .15s; font-family: inherit;
  }
  .subj-chip:hover { border-color: rgba(34,197,94,.4); color: #4ade80; }
  .subj-chip-on {
    background: rgba(34,197,94,.12) !important; border-color: #4ade80 !important;
    color: #4ade80 !important;
  }
  .all-subj-note {
    background: rgba(34,197,94,.08); border: 1px solid rgba(34,197,94,.2);
    border-radius: 9px; padding: 8px 11px; font-size: 12px; color: #4ade80; font-weight: 600;
  }
  .all-subj-sub { font-size:10px; color:#94a3b8; font-weight:400; margin-top:2px; }
  .hint-text { font-size:12px; color:#94a3b8; }

  /* Filter input */
  .filter-input {
    width: 100%; padding: 10px 12px; border-radius: 9px;
    border: 1px solid #1e1e3a; background: #080812; color: #e2e8f0;
    font-size: 13px; font-family: inherit; transition: border-color .2s, box-shadow .2s;
  }
  .filter-input:focus { outline:none; border-color:#f97316; box-shadow:0 0 0 3px rgba(249,115,22,.12); }
  .filter-input::placeholder { color:#4a5568; }

  /* Fee input */
  .fee-input-wrap { position: relative; }
  .fee-prefix {
    position: absolute; left: 11px; top: 50%; transform: translateY(-50%);
    color: #cbd5e1; font-size: 13px; font-weight: 600; pointer-events: none;
  }
  .fee-input { padding-left: 24px !important; }
  .fee-hint { font-size: 10px; color: #94a3b8; margin-top: 5px; }

  /* Reset btn in sidebar */
  .reset-all-btn {
    width: calc(100% - 36px); margin: 10px 18px 4px;
    padding: 10px; background: transparent; border: 1px solid #252548;
    border-radius: 10px; color: #94a3b8; font-size: 12px; font-weight: 600;
    cursor: pointer; font-family: inherit; transition: all .18s;
  }
  .reset-all-btn:hover { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,.05); }

  /* ── Results main ── */
  .sp-results { min-width: 0; }

  /* Active filter chips bar */
  .af-bar {
    display: flex; gap: 6px; flex-wrap: wrap; align-items: center;
    margin-bottom: 14px;
  }
  .af-label { font-size: 10px; color: #94a3b8; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; }
  .af-chip {
    background: rgba(249,115,22,.1); color: #fb923c; font-size: 11px;
    padding: 4px 10px; border-radius: 20px; border: 1px solid rgba(249,115,22,.25);
    font-weight: 600; display: flex; align-items: center; gap: 5px;
  }
  .af-x { cursor:pointer; opacity:.55; font-size:10px; }
  .af-x:hover { opacity:1; }

  /* Results meta */
  .results-meta {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 16px;
  }
  .results-count { font-size: 14px; color: #cbd5e1; }
  .results-count strong { color: #f97316; font-size: 16px; font-weight: 800; margin-right: 2px; }
  .live-badge { display:flex; align-items:center; gap:5px; font-size:11px; color:#cbd5e1; }
  .live-dot { width:6px; height:6px; border-radius:50%; background:#22c55e; animation:pulse-dot 2s ease infinite; }

  /* Idle state */
  .idle-state {
    text-align: center; padding: 60px 20px;
    border: 1px dashed #1e1e3a; border-radius: 18px;
  }
  .idle-icon { font-size: 48px; margin-bottom: 16px; }
  .idle-state h3 { font-size:18px; font-weight:700; color:#fff; margin:0 0 8px; }
  .idle-state p { font-size:13px; color:#64748b; margin:0; line-height:1.7; }
  .idle-steps {
    display: flex; gap: 20px; justify-content: center; flex-wrap: wrap; margin-top: 24px;
  }
  .idle-step {
    display: flex; align-items: center; gap: 7px; font-size: 12px; color: #4a5568;
  }
  .idle-step-num {
    width: 22px; height: 22px; border-radius: 50%; background: #1a1a30;
    color: #f97316; font-size: 10px; font-weight: 800;
    display: flex; align-items: center; justify-content: center;
  }

  /* ── Teacher card ── */
  .tcard {
    background: #0d0d1f; border: 1px solid #1a1a35; border-radius: 16px;
    padding: 18px 20px; margin-bottom: 10px;
    animation: fadeInUp .3s ease both; transition: border-color .22s, box-shadow .22s, transform .2s;
  }
  .tcard:hover { border-color: rgba(249,115,22,.4); box-shadow:0 8px 32px rgba(249,115,22,.1); transform:translateY(-2px); }
  .tcard-inner { display:flex; gap:14px; align-items:flex-start; }
  .tcard-mid { flex:1; min-width:0; }
  .tcard-right { flex-shrink:0; display:flex; flex-direction:column; align-items:flex-end; gap:6px; }

  .tcard-name {
    font-size:15px; font-weight:700; color:#fff;
    display:flex; align-items:center; gap:6px; flex-wrap:wrap;
    cursor:pointer; margin-bottom:3px; transition:color .15s;
  }
  .tcard-name:hover { color:#f97316; }
  .tcard-loc { font-size:12px; color:#94a3b8; margin-bottom:8px; }
  .tcard-chips { display:flex; gap:5px; flex-wrap:wrap; margin-bottom:8px; }
  .tcard-chip {
    background:rgba(249,115,22,.08); color:#fb923c; font-size:11px;
    padding:3px 9px; border-radius:6px; font-weight:600; border:1px solid rgba(249,115,22,.2);
  }
  .tcard-meta { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:12px; }
  .tcard-exp { font-size:11px; color:#cbd5e1; background:#111827; padding:3px 9px; border-radius:6px; font-weight:500; }
  .tcard-demo { font-size:11px; color:#4ade80; background:rgba(74,222,128,.08); border:1px solid rgba(74,222,128,.2); padding:3px 9px; border-radius:6px; font-weight:600; }
  .tcard-stars { color:#fbbf24; font-size:12px; }
  .tcard-rcount { font-size:11px; color:#64748b; }
  .tcard-rating { display:flex; align-items:center; gap:4px; }

  .tcard-fee { font-size:16px; font-weight:800; color:#f97316; white-space:nowrap; text-align:right; }
  .tcard-fee-sub { font-size:10px; font-weight:400; color:#64748b; }
  .avail-badge {
    font-size:10px; padding:4px 9px; border-radius:20px; font-weight:600;
    white-space:nowrap; display:flex; align-items:center; gap:4px;
  }
  .avail-dot { width:5px; height:5px; border-radius:50%; }
  .view-btn {
    padding:8px 16px; background:#f97316; color:#fff; border:none; border-radius:8px;
    font-size:12px; font-weight:700; cursor:pointer; font-family:inherit;
    transition: box-shadow .18s, transform .15s; white-space:nowrap;
  }
  .view-btn:hover { box-shadow:0 4px 14px rgba(249,115,22,.45); transform:translateY(-1px); }

  /* Skeleton */
  .skeleton {
    background:linear-gradient(90deg,#0d0d1f 25%,#181830 50%,#0d0d1f 75%);
    background-size:600px 100%; animation:shimmer 1.4s ease infinite; border-radius:8px;
  }

  /* Empty state */
  .empty-divider { display:flex; align-items:center; gap:12px; margin-bottom:18px; }
  .empty-line { flex:1; height:1px; background:#1a1a35; }
  .empty-label { font-size:10px; color:#64748b; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; white-space:nowrap; }

  /* Lead form */
  .lead-card {
    background:#0d0d1f; border-radius:16px;
    border:1px solid rgba(249,115,22,.2); padding:24px 20px;
  }
  .lead-label { font-size:11px; font-weight:600; color:#94a3b8; letter-spacing:1px; text-transform:uppercase; margin-bottom:6px; }
  .lead-input {
    width:100%; padding:11px 14px; border-radius:10px;
    background:#080812; color:#e2e8f0; font-size:14px;
    font-family:inherit; box-sizing:border-box; transition:border-color .2s, box-shadow .2s;
  }
  .lead-input:focus { outline:none; border-color:#f97316 !important; box-shadow:0 0 0 3px rgba(249,115,22,.12) !important; }
  .lead-input::placeholder { color:#4a5568; }
  .locality-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .submit-btn {
    width:100%; padding:14px; background:#f97316; color:#fff; border:none;
    border-radius:10px; font-size:15px; font-weight:700; cursor:pointer;
    font-family:inherit; transition:box-shadow .18s, opacity .18s;
  }
  .submit-btn:disabled { opacity:.65; cursor:not-allowed; }
  .submit-btn:not(:disabled):hover { box-shadow:0 4px 16px rgba(249,115,22,.4); }

  /* Fetch error */
  .fetch-err {
    background:rgba(239,68,68,.05); border:1px solid rgba(239,68,68,.2); border-radius:10px;
    margin-bottom:16px; padding:12px 16px; display:flex; align-items:center; gap:10px;
  }

  /* ── Responsive ── */
  @media (max-width: 900px) {
    .sp-layout { grid-template-columns: 1fr; padding: 16px 16px 40px; gap: 20px; }
    .sp-sidebar { position: static; }
  }
  @media (max-width: 640px) {
    .tcard-inner { flex-direction:column; gap:10px; }
    .tcard-right { flex-direction:row; align-items:center; justify-content:space-between; width:100%; }
    .tcard-fee { font-size:14px; }
    .class-grid { grid-template-columns: repeat(3, 1fr); }
    .locality-grid { grid-template-columns:1fr; }
    .idle-steps { flex-direction:column; align-items:center; gap:12px; }
  }

  ::-webkit-scrollbar { width:4px; }
  ::-webkit-scrollbar-track { background:#080812; }
  ::-webkit-scrollbar-thumb { background:#f97316; border-radius:4px; }
`;

// ─── Avatar ───────────────────────────────────────────────────────────────────
const Avatar = ({ teacher, size = 50 }) => {
  const initials = (teacher.name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  if (teacher.photo_url) return (
    <img src={teacher.photo_url} alt={teacher.name}
      style={{ width: size, height: size, borderRadius: 12, objectFit: "cover", border: "2px solid #1e1e3a", flexShrink: 0 }} />
  );
  const colors = ["#f97316", "#ea580c", "#c2410c", "#b45309", "#92400e"];
  const bg = colors[(teacher.name?.charCodeAt(0) || 0) % colors.length];
  return (
    <div style={{ width: size, height: size, borderRadius: 12, background: bg, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: size * 0.32, flexShrink: 0, border: "2px solid #1e1e3a" }}>
      {initials}
    </div>
  );
};

// ─── SkeletonCard ─────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div style={{ background: "#0d0d1f", border: "1px solid #1a1a35", borderRadius: 16, padding: "18px 20px", marginBottom: 10 }}>
    <div style={{ display: "flex", gap: 14 }}>
      <div className="skeleton" style={{ width: 50, height: 50, borderRadius: 12, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div className="skeleton" style={{ height: 16, width: "40%", marginBottom: 8 }} />
        <div className="skeleton" style={{ height: 11, width: "28%", marginBottom: 12 }} />
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          <div className="skeleton" style={{ height: 20, width: 70, borderRadius: 6 }} />
          <div className="skeleton" style={{ height: 20, width: 90, borderRadius: 6 }} />
        </div>
        <div className="skeleton" style={{ height: 30, width: 110, borderRadius: 8 }} />
      </div>
    </div>
  </div>
);

// ─── LeadForm (unchanged) ─────────────────────────────────────────────────────
const LeadForm = ({ classLevel, subjects }) => {
  const [form, setForm] = useState({ name: "", phone: "", class: classLevel || "", locality: "", city: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [fieldErr, setFieldErr] = useState({});

  const submit = async () => {
    const fe = {};
    if (!form.name.trim())     fe.name     = "Name is required";
    if (!form.phone.trim())    fe.phone    = "WhatsApp number is required";
    else if (form.phone.replace(/\D/g, "").length < 10) fe.phone = "Enter a valid 10-digit number";
    if (!form.class)           fe.class    = "Please select a class";
    if (!form.locality.trim()) fe.locality = "Locality is required";
    if (Object.keys(fe).length) { setFieldErr(fe); return; }
    setFieldErr({});
    setSubmitting(true);
    setErr("");
    try {
      await api.post("/leads", { name: form.name.trim(), phone: form.phone.trim(), class: form.class, subjects: subjects || [], city: form.city.trim(), locality: form.locality.trim() });
      setSubmitted(true);
    } catch (e) {
      setErr(e.response?.data?.message || "Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  if (submitted) return (
    <div className="lead-card" style={{ border: "1px solid rgba(34,197,94,.25)", textAlign: "center" }}>
      <div style={{ fontSize: 44, marginBottom: 12 }}>🎉</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#22c55e", marginBottom: 8 }}>Request received!</div>
      <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.8, maxWidth: 280, margin: "0 auto" }}>
        We'll personally find the right tutor and reach out on <span style={{ color: "#f97316", fontWeight: 700 }}>{form.phone}</span> within 24 hours.
      </div>
    </div>
  );

  return (
    <div className="lead-card">
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🙋</div>
        <div style={{ fontSize: 17, fontWeight: 800, color: "#ffffff", marginBottom: 6 }}>Can't find the right tutor?</div>
        <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.7, maxWidth: 320, margin: "0 auto" }}>
          Leave your details — we'll personally match you
          {classLevel && <span style={{ color: "#f97316", fontWeight: 600 }}> for {classLevel}</span>}
          {subjects?.length > 0 && <span style={{ color: "#f97316", fontWeight: 600 }}> ({subjects.join(", ")})</span>}
          {" "}and reach out within 24 hours.
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 14 }}>
        <div>
          <div className="lead-label">Your Name <span style={{ color: "#ef4444" }}>*</span></div>
          <input className="lead-input" value={form.name} maxLength={100}
            onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setFieldErr(fe => ({ ...fe, name: "" })); }}
            placeholder="e.g. Rahul Sharma"
            style={{ border: `1px solid ${fieldErr.name ? "#ef4444" : "#252545"}`, background: fieldErr.name ? "#1a0505" : "#080812" }} />
          {fieldErr.name && <div style={{ color: "#ef4444", fontSize: 11, marginTop: 4 }}>⚠ {fieldErr.name}</div>}
        </div>
        <div>
          <div className="lead-label">WhatsApp Number <span style={{ color: "#ef4444" }}>*</span></div>
          <input className="lead-input" value={form.phone} type="tel" maxLength={10} inputMode="numeric"
            onChange={e => { const v = e.target.value.replace(/\D/g, "").slice(0, 10); setForm(f => ({ ...f, phone: v })); setFieldErr(fe => ({ ...fe, phone: "" })); }}
            placeholder="e.g. 9876500000"
            style={{ border: `1px solid ${fieldErr.phone ? "#ef4444" : "#252545"}`, background: fieldErr.phone ? "#1a0505" : "#080812" }} />
          {fieldErr.phone && <div style={{ color: "#ef4444", fontSize: 11, marginTop: 4 }}>⚠ {fieldErr.phone}</div>}
        </div>
        <div>
          <div className="lead-label">Class / Grade <span style={{ color: "#ef4444" }}>*</span></div>
          <select value={form.class}
            onChange={e => { setForm(f => ({ ...f, class: e.target.value })); setFieldErr(fe => ({ ...fe, class: "" })); }}
            style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: `1px solid ${fieldErr.class ? "#ef4444" : "#252545"}`, background: fieldErr.class ? "#1a0505" : "#080812", color: form.class ? "#e2e8f0" : "#334155", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box", appearance: "none" }}>
            <option value="">Select class…</option>
            {CLASS_GROUPS.map(g => (
              <optgroup key={g.group} label={g.group}>
                {g.classes.map(c => <option key={`${g.group}-${c}`} value={c}>{c}</option>)}
              </optgroup>
            ))}
          </select>
          {fieldErr.class && <div style={{ color: "#ef4444", fontSize: 11, marginTop: 4 }}>⚠ {fieldErr.class}</div>}
        </div>
        <div className="locality-grid">
          <div>
            <div className="lead-label">Locality <span style={{ color: "#ef4444" }}>*</span></div>
            <input className="lead-input" value={form.locality} maxLength={100}
              onChange={e => { setForm(f => ({ ...f, locality: e.target.value })); setFieldErr(fe => ({ ...fe, locality: "" })); }}
              placeholder="Kidwai Nagar"
              style={{ border: `1px solid ${fieldErr.locality ? "#ef4444" : "#252545"}`, background: fieldErr.locality ? "#1a0505" : "#080812" }} />
            {fieldErr.locality && <div style={{ color: "#ef4444", fontSize: 11, marginTop: 4 }}>⚠ {fieldErr.locality}</div>}
          </div>
          <div>
            <div className="lead-label">City <span style={{ color: "#4a5568", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></div>
            <input className="lead-input" value={form.city} maxLength={100}
              onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
              placeholder="Kanpur"
              style={{ border: "1px solid #252545", background: "#080812" }} />
          </div>
        </div>
      </div>
      {err && (
        <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12, padding: "10px 14px", background: "rgba(239,68,68,.07)", borderRadius: 8, border: "1px solid rgba(239,68,68,.2)" }}>
          ⚠️ {err}
        </div>
      )}
      <button className="submit-btn" onClick={submit} disabled={submitting}>
        {submitting ? "Submitting…" : "Notify Me When Available →"}
      </button>
      <div style={{ textAlign: "center", fontSize: 11, color: "#334155", marginTop: 10 }}>
        🔒 Your number is only used to find you a tutor. No spam.
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
export default function Search() {
  const [teachers,     setTeachers]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [fetchError,   setFetchError]   = useState("");
  const [classLevel,   setClassLevel]   = useState("");
  const [board,        setBoard]        = useState("");
  const [subjects,     setSubjects]     = useState([]);
  const [area,         setArea]         = useState("");
  const [maxFee,       setMaxFee]       = useState("");
  const [teacherName,  setTeacherName]  = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [classOpen,    setClassOpen]    = useState(false);
  const [currentPage,  setCurrentPage]  = useState(1);
  const [totalPages,   setTotalPages]   = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const nav = useNavigate();

  // Derived values
  const classNum      = parseInt(classLevel?.match(/\d+/)?.[0]) || 0;
  const isLowerClass  = !classLevel || classNum === 0 || classNum <= 8;
  const availSubjects = classLevel && !isLowerClass ? getSubjectsForClass(classLevel) : [];
  const hasApiFilter = !!(classLevel || subjects.length || maxFee || area.trim() || board || verifiedOnly || teacherName.trim());
  const hasFilter    = hasApiFilter;

  useEffect(() => { setSubjects([]); setCurrentPage(1); }, [classLevel]);
  useEffect(() => { setCurrentPage(1); }, [maxFee, subjects, area, board, verifiedOnly, teacherName]);

  useEffect(() => {
    setLoading(true);
    setFetchError("");
    const params = { page: currentPage, limit: 10 };
    if (maxFee)               params.max_fee    = maxFee;
    if (area.trim())          params.area       = area.trim();
    if (classLevel)           params.classLevel = classLevel;
    if (subjects.length)      params.subject    = subjects[0];
    if (board)                params.board      = board;
    if (verifiedOnly)         params.verified   = "true";
    if (teacherName.trim())   params.name       = teacherName.trim();
    const timer = setTimeout(() => {
      api.get("/teachers", { params })
        .then(r => {
          setTeachers(Array.isArray(r.data.teachers) ? r.data.teachers : []);
          setTotalPages(r.data.totalPages || 1);
          setTotalResults(r.data.totalResults || 0);
        })
        .catch(() => { setTeachers([]); setFetchError("Failed to load tutors. Please try again."); })
        .finally(() => setLoading(false));
    }, hasApiFilter ? 320 : 0);
    return () => clearTimeout(timer);
  }, [maxFee, currentPage, area, classLevel, subjects, board, verifiedOnly, teacherName, hasApiFilter]);

  const toggleSubject = s =>
    setSubjects(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const displayList = teachers.filter(t => teacherMatchesSearch(t, classLevel, subjects));

  const clearAll = () => {
    setClassLevel(""); setBoard(""); setSubjects([]);
    setArea(""); setMaxFee(""); setTeacherName(""); setVerifiedOnly(false);
    setClassOpen(false); setCurrentPage(1);
  };

  const seoTitle = (() => {
    const parts = [];
    if (subjects.length) parts.push(subjects.slice(0, 2).join(" & "));
    if (classLevel) parts.push(`Class ${classLevel}`);
    const prefix = parts.length ? `${parts.join(" ")} ` : "";
    return `${prefix}Home Tutors in Kanpur – Find Verified Teachers | PadhaiPass`;
  })();
  const seoDesc = `Browse verified home tutors in Kanpur for ${subjects.length ? subjects.slice(0, 2).join(", ") : "all subjects"}, ${classLevel ? `class ${classLevel}` : "all classes"}. Filter by area, fee, and availability.`;

  // ── Filter panel JSX (shared between sidebar and mobile drawer) ──────────────
  const FilterPanel = () => (
    <div className="filter-content">

      {/* Step 1: Class */}
      <div className="filter-step">
        <div className={`step-num ${classLevel ? "step-done" : ""}`}>1</div>
        <div className="step-body">
          <div className="step-label">Class / Grade</div>

          {/* Selected class chip — shown when class is picked */}
          {classLevel && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: classOpen ? 0 : 0 }}>
              <span className="class-selected-chip">
                {classLevel}
                <span className="class-clear-x" onClick={() => { setClassLevel(""); setSubjects([]); setClassOpen(false); }}>✕</span>
              </span>
              <button onClick={() => setClassOpen(o => !o)}
                style={{ fontSize: 11, color: "#64748b", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", padding: "2px 6px" }}>
                {classOpen ? "▲ hide" : "▼ change"}
              </button>
            </div>
          )}

          {/* Collapsed prompt — shown when no class selected and grid closed */}
          {!classLevel && !classOpen && (
            <button className="class-select-btn" onClick={() => setClassOpen(true)}>
              <span>Tap to select class…</span>
              <span style={{ fontSize: 10 }}>▼</span>
            </button>
          )}

          {/* Grid — shown when open */}
          {classOpen && (
            <div className="class-grid">
              {ALL_CLASSES.map(c => {
                const label = c.replace("Class ", "");
                return (
                  <button key={c}
                    className={`class-chip${classLevel === c ? " class-chip-on" : ""}`}
                    onClick={() => { setClassLevel(prev => prev === c ? "" : c); setSubjects([]); setClassOpen(false); }}>
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Step 2: Board */}
      <div className="filter-step">
        <div className={`step-num ${board ? "step-done" : ""}`}>2</div>
        <div className="step-body">
          <div className="step-label">Board <span className="opt-tag">optional</span></div>
          <div className="board-chips">
            {BOARDS.map(b => (
              <button key={b}
                className={`board-chip${board === b ? " board-chip-on" : ""}`}
                onClick={() => setBoard(prev => prev === b ? "" : b)}>
                {b}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Step 3: Subject (conditional) */}
      <div className="filter-step">
        <div className={`step-num ${subjects.length > 0 ? "step-done" : ""}`}>3</div>
        <div className="step-body">
          <div className="step-label">Subject</div>
          {!classLevel && (
            <div className="hint-text">Select a class first</div>
          )}
          {classLevel && isLowerClass && (
            <div className="all-subj-note">
              📚 All Subjects
              <div className="all-subj-sub">Auto-applied for {classLevel}</div>
            </div>
          )}
          {classLevel && !isLowerClass && (
            <div className="subj-chips">
              <button className={`subj-chip${subjects.length === 0 ? " subj-chip-on" : ""}`}
                onClick={() => setSubjects([])}>All</button>
              {availSubjects.map(s => (
                <button key={s}
                  className={`subj-chip${subjects.includes(s) ? " subj-chip-on" : ""}`}
                  onClick={() => toggleSubject(s)}>
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Step 4: Area */}
      <div className="filter-step">
        <div className={`step-num ${area.trim() ? "step-done" : ""}`}>4</div>
        <div className="step-body">
          <div className="step-label">Area / Locality</div>
          <input className="filter-input" value={area}
            onChange={e => { setArea(e.target.value); setCurrentPage(1); }}
            placeholder="e.g. Kidwai Nagar, Civil Lines…" />
        </div>
      </div>

      {/* Step 5: Max Fee */}
      <div className="filter-step">
        <div className={`step-num ${maxFee ? "step-done" : ""}`}>5</div>
        <div className="step-body">
          <div className="step-label">Max Monthly Fee</div>
          <div className="fee-input-wrap">
            <span className="fee-prefix">₹</span>
            <input className="filter-input fee-input" type="number" min={0}
              value={maxFee}
              onChange={e => { setMaxFee(e.target.value); setCurrentPage(1); }}
              placeholder="e.g. 2000" />
          </div>
          <div className="fee-hint">Only tutors within your budget</div>
        </div>
      </div>

      {/* Step 6: Teacher Name */}
      <div className="filter-step">
        <div className={`step-num ${teacherName.trim() ? "step-done" : ""}`}>6</div>
        <div className="step-body">
          <div className="step-label">Teacher Name <span className="opt-tag">optional</span></div>
          <input className="filter-input" value={teacherName}
            onChange={e => setTeacherName(e.target.value)}
            placeholder="Search by name…" />
        </div>
      </div>

      {/* Verified filter */}
      <div className="filter-step">
        <div className={`step-num ${verifiedOnly ? "step-done" : ""}`}>✓</div>
        <div className="step-body">
          <div className="step-label">Teacher Type</div>
          <select
            value={verifiedOnly ? "verified" : "all"}
            onChange={e => { setVerifiedOnly(e.target.value === "verified"); setCurrentPage(1); }}
            style={{
              width:"100%", padding:"9px 12px", borderRadius:8, fontSize:13, fontWeight:600,
              background:"#0f0f1a", border:"1px solid #1e1e3a",
              color: verifiedOnly ? "#22c55e" : "#e2e8f0",
              outline:"none", cursor:"pointer", fontFamily:"inherit",
            }}>
            <option value="all" style={{ color:"#e2e8f0", background:"#0f0f1a" }}>All Teachers</option>
            <option value="verified" style={{ color:"#22c55e", background:"#0f0f1a" }}>✓ Verified Only</option>
          </select>
        </div>
      </div>

      {hasFilter && (
        <button className="reset-all-btn" onClick={clearAll}>✕ Reset All Filters</button>
      )}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="sp">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href="https://www.padhaipass.com/search" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
      </Helmet>
      <style>{styles}</style>

      {/* Header */}
      <div className="sp-header">
        <h1>Find Your <span>Perfect Tutor</span></h1>
        <p>Verified home tutors in Kanpur — koi hidden charge nahi</p>
      </div>

      {/* Main layout */}
      <div className="sp-layout">

        {/* Sidebar — desktop only */}
        <aside className="sp-sidebar">
          <div className="sidebar-head">
            <span className="sidebar-title">🎛 Filters</span>
            {hasFilter && (
              <button className="reset-link" onClick={clearAll}>Reset all</button>
            )}
          </div>
          {FilterPanel()}
        </aside>

        {/* Results */}
        <main className="sp-results">

          {/* Active filter chips */}
          {hasFilter && (
            <div className="af-bar">
              <span className="af-label">Active:</span>
              {classLevel && <span className="af-chip">{classLevel} <span className="af-x" onClick={() => setClassLevel("")}>✕</span></span>}
              {board && <span className="af-chip">{board} <span className="af-x" onClick={() => setBoard("")}>✕</span></span>}
              {verifiedOnly && <span className="af-chip" style={{ color:"#22c55e", borderColor:"#22c55e" }}>✓ Verified <span className="af-x" onClick={() => setVerifiedOnly(false)}>✕</span></span>}
              {area.trim() && <span className="af-chip">📍 {area} <span className="af-x" onClick={() => setArea("")}>✕</span></span>}
              {subjects.map(s => <span key={s} className="af-chip">{s} <span className="af-x" onClick={() => toggleSubject(s)}>✕</span></span>)}
              {maxFee && <span className="af-chip">≤ ₹{Number(maxFee).toLocaleString()} <span className="af-x" onClick={() => setMaxFee("")}>✕</span></span>}
              {teacherName.trim() && <span className="af-chip">"{teacherName}" <span className="af-x" onClick={() => setTeacherName("")}>✕</span></span>}
            </div>
          )}

          {/* Results meta */}
          <div className="results-meta">
            <div className="results-count">
              {loading ? (
                <span style={{ color: "#f97316" }}>Finding tutors…</span>
              ) : (
                <>
                  <strong>{totalResults}</strong>
                  <span> tutor{totalResults !== 1 ? "s" : ""} {hasFilter ? "found" : "available"}</span>
                  {classLevel && <span style={{ color: "#f97316" }}> for {classLevel}</span>}
                  {subjects.length > 0 && <span style={{ color: "#94a3b8" }}> — {subjects.join(", ")}</span>}
                </>
              )}
            </div>
            {!loading && totalResults > 0 && (
              <div className="live-badge">
                <span className="live-dot" /> {hasFilter ? "Live results" : "Verified first"}
              </div>
            )}
          </div>

          {/* Fetch error */}
          {fetchError && (
            <div className="fetch-err">
              <span style={{ fontSize: 18 }}>⚠️</span>
              <span style={{ color: "#ef4444", fontSize: 13, flex: 1 }}>{fetchError}</span>
              <button onClick={() => window.location.reload()}
                style={{ background: "#ef4444", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                Refresh
              </button>
            </div>
          )}

          {/* Skeletons */}
          {loading && [1, 2, 3].map(i => <SkeletonCard key={i} />)}

          {/* Teacher cards */}
          {!loading && (() => {
            const verifiedList   = displayList.filter(t => t.verified);
            const unverifiedList = displayList.filter(t => !t.verified);
            const showDivider    = verifiedList.length > 0 && unverifiedList.length > 0;
            return [...verifiedList, ...unverifiedList].map((t, i) => {
              const isFirstUnverified = showDivider && i === verifiedList.length;
              const rating = t.rating_avg || avgR(t.ratings);
              const { compact } = formatTeachingStructure(t.teaching, t.classes, t.subjects);
              return (<React.Fragment key={t._id}>
                {isFirstUnverified && (
                  <div className="empty-divider" style={{ margin: "18px 0 14px" }}>
                    <div className="empty-line" />
                    <span className="empty-label">Other Tutors</span>
                    <div className="empty-line" />
                  </div>
                )}
              <div className="tcard" style={{ animationDelay: i * 0.05 + "s" }}>
                <div className="tcard-inner">
                  <Avatar teacher={t} size={50} />
                  <div className="tcard-mid">
                    <div className="tcard-name" onClick={() => nav("/teacher/" + t._id)}>
                      {t.name}
                      {t.verified && <VerifiedBadge />}
                    </div>
                    <div className="tcard-loc">📍 {t.area}{t.city ? ", " + t.city : ""}</div>

                    {(() => {
                      if (compact) return (
                        <div className="tcard-chips">
                          {compact.split(" | ").map((p, idx) => <span key={idx} className="tcard-chip">{p}</span>)}
                        </div>
                      );
                      if (t.teaching?.length > 0) return (
                        <div className="tcard-chips">
                          {t.teaching.map((e, idx) => <span key={idx} className="tcard-chip">{e.class}</span>)}
                        </div>
                      );
                      if (t.classes?.length > 0) return (
                        <div className="tcard-chips">
                          {(Array.isArray(t.classes) ? t.classes : [t.classes]).map((c, idx) => <span key={idx} className="tcard-chip">{c}</span>)}
                        </div>
                      );
                      return null;
                    })()}

                    <div className="tcard-meta">
                      {t.experience && <span className="tcard-exp">{t.experience} yr{t.experience > 1 ? "s" : ""} exp</span>}
                      {t.offers_demo_class && <span className="tcard-demo">✓ Free Demo</span>}
                      {(t.ratings?.length > 0 || t.rating_avg > 0) && (
                        <div className="tcard-rating">
                          <span className="tcard-stars">{"★".repeat(Math.floor(rating))}{"☆".repeat(5 - Math.floor(rating))}</span>
                          <span className="tcard-rcount">{rating} ({t.ratings?.length || 0})</span>
                        </div>
                      )}
                    </div>

                    <button className="view-btn" onClick={() => nav("/teacher/" + t._id)}>
                      View Profile →
                    </button>
                  </div>

                  <div className="tcard-right">
                    <div className="avail-badge" style={{
                      background: t.available ? "rgba(34,197,94,.08)" : "rgba(239,68,68,.08)",
                      color: t.available ? "#22c55e" : "#ef4444",
                      border: `1px solid ${t.available ? "rgba(34,197,94,.2)" : "rgba(239,68,68,.2)"}`,
                    }}>
                      <span className="avail-dot" style={{ background: t.available ? "#22c55e" : "#ef4444", animation: t.available ? "pulse-dot 2s ease infinite" : "none" }} />
                      {t.available ? "Available" : "Busy"}
                    </div>
                    <div className="tcard-fee">
                      {t.fees_per_hour
                        ? <>₹{t.fees_per_hour}<span className="tcard-fee-sub">/mo</span></>
                        : <span style={{ color: "#334155", fontSize: 12 }}>—</span>}
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>);
          });
          })()}

          {/* Empty state with lead form */}
          {!loading && displayList.length === 0 && (
            <div style={{ paddingBottom: 20 }}>
              <div className="empty-divider">
                <div className="empty-line" />
                <span className="empty-label">No tutors found — leave your details</span>
                <div className="empty-line" />
              </div>
              <LeadForm classLevel={classLevel} subjects={subjects} />
            </div>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={p => { setCurrentPage(p); window.scrollTo(0, 0); }}
          />
          <div style={{ height: 32 }} />
        </main>
      </div>
    </div>
  );
}
